import { InventoryItem } from "../components/ui/InventoryItem";
import { inventoryManager } from "../managers/InventoryManager";
import { itemManager } from "../managers/ItemManager";
import { getStatsManager } from "../managers/StatsManager";
import { ContextMenu } from "../components/ui/ContextMenu";

export default class InventoryScene extends Phaser.Scene {
    constructor() {
        super({ key: 'InventoryScene' });
        this.slots = [];
        this.tooltip = null;
        this.draggingItem = null;
        this.contextMenu = null;
        this.statsManager = null;
        
        // Crop consumption tracking for 3-crop hunger system
        this.cropConsumption = {
            count: 0,
            timer: null,
            TIMER_DURATION: 5000 // 5 seconds
        };
    }

    init() {
        // Get user data from game registry
        const gameStore = this.game.registry.get('gameStore');
        const { userId } = gameStore;

        if (userId && inventoryManager.shouldSync()) {
            inventoryManager.syncWithDatabase();
        }
    }

    preload() {
        if (!this.textures.exists('closeButton')) {
            this.load.image('closeButton', 'path/to/closeButton.png');
        }

        // Load all item textures and frames
        itemManager.preloadAssets(this);

        // Load eat sound effect
        if (!this.cache.audio.exists('eatItem')) {
            this.load.audio('eatItem', '/audio/sfx/inventory/eat-item.mp3');
        }

        // Add load complete handler
        this.load.on('complete', () => {
            console.log('All textures loaded successfully');
        });
    }

    create() {
        this.input.mouse.disableContextMenu();

        // Create inventory background
        this.add.rectangle(1, 1, 254, 192, 0xd0965b)
            .setOrigin(0, 0);

        // Create grid slots
        for (let row = 0; row < 5; row++) {
            for (let col = 0; col < 7; col++) {
                const pixelX = col * 32 + 32;
                const pixelY = row * 32 + 32;
                
                // Create slot background
                const slot = this.add.rectangle(pixelX, pixelY, 30, 30, 0x884f32)
                    .setOrigin(0.5);

                // Create inset shadow (darker inside)
                this.add.rectangle(pixelX, pixelY, 28, 28, 0x000000)
                    .setOrigin(0.5)
                    .setAlpha(0.2);

                // Add subtle inner highlight on top/left
                this.add.line(pixelX - 13, pixelY - 13, 0, 0, 26, 0, 0xffffff)
                    .setOrigin(0)
                    .setAlpha(0.1);
                this.add.line(pixelX - 13, pixelY - 13, 0, 0, 0, 26, 0xffffff)
                    .setOrigin(0)
                    .setAlpha(0.1);

                // Add darker inner edge on bottom/right
                this.add.line(pixelX - 13, pixelY + 13, 0, 0, 26, 0, 0x000000)
                    .setOrigin(0)
                    .setAlpha(0.3);
                this.add.line(pixelX + 13, pixelY - 13, 0, 0, 0, 26, 0x000000)
                    .setOrigin(0)
                    .setAlpha(0.3);

                // Store both pixel and grid coordinates
                this.slots.push({
                    x: pixelX,
                    y: pixelY,
                    gridX: col,
                    gridY: row,
                    rect: slot,
                    occupied: false,
                    item: null
                });
            }
        }
        
        this.setupUI();
        this.setupDragEvents();
        this.setupTooltip();
        this.setupContextMenu();
        
        // Initialize stats manager connection
        this.statsManager = getStatsManager();
        
        // Create money display
        this.createMoneyDisplay();
        
        // Populate inventory from manager
        this.populateInventory();

        // Subscribe to inventory changes
        inventoryManager.events.on('inventory-updated', this.handleInventoryUpdate, this);
    }
    
    createMoneyDisplay() {
        // Create money background panel at bottom of inventory
        const moneyBg = this.add.rectangle(128, 185, 240, 20, 0x8B4513)
            .setStrokeStyle(2, 0x654321);
        
        // Add money icon/symbol and text
        this.moneyText = this.add.text(20, 185, '¥0', {
            fontSize: '14px',
            fontFamily: 'Arial Black',
            color: '#FFD700',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0, 0.5);
        
        // Update money display with current amount
        this.updateMoneyDisplay();
    }
    
    updateMoneyDisplay() {
        if (this.statsManager) {
            const stats = this.statsManager.getStats();
            if (stats && stats.money !== undefined) {
                this.moneyText.setText(`¥${stats.money.toLocaleString()}`);
            }
        }
    }

    setupUI() {
        const PLAY_AREA_WIDTH = 256;
        const PADDING = 16;
        const menuWidth = PLAY_AREA_WIDTH - (PADDING * 2);
        
        const closeButton = this.add.image(
            PADDING + menuWidth + 5,
            10,
            'closeButton'
        )
        .setScale(1)
        .setInteractive();

        closeButton.on('pointerup', () => {
            this.scene.sleep();
        });
    }

    setupDragEvents() {
        this.input.on('dragstart', (pointer, gameObject) => {
            this.draggingItem = gameObject;
            gameObject.setTint(0x999999);
            this.tooltip.setVisible(false);
        });

        this.input.on('drag', (pointer, gameObject, dragX, dragY) => {
            gameObject.x = dragX;
            gameObject.y = dragY;
        });

        this.input.on('dragend', (pointer, gameObject) => {
            this.draggingItem = null;
            gameObject.clearTint();
            
            let closest = null;
            let closestDistance = Number.MAX_VALUE;
            let targetItem = null;
            
            this.slots.forEach(slot => {
                const distance = Phaser.Math.Distance.Between(
                    gameObject.x,
                    gameObject.y,
                    slot.x,
                    slot.y
                );
                
                if (distance < closestDistance) {
                    closest = slot;
                    closestDistance = distance;
                    targetItem = slot.item;
                }
            });
            
            if (closest && closestDistance < 32) {
                if (targetItem) {
                    // Swap items
                    const originalSlot = this.getSlotAtPosition(gameObject.originalX, gameObject.originalY);
                    
                    targetItem.x = gameObject.originalX;
                    targetItem.y = gameObject.originalY;
                    targetItem.originalX = gameObject.originalX;
                    targetItem.originalY = gameObject.originalY;
                    
                    gameObject.x = closest.x;
                    gameObject.y = closest.y;
                    gameObject.originalX = closest.x;
                    gameObject.originalY = closest.y;
                    
                    originalSlot.item = targetItem;
                    closest.item = gameObject;

                    // Update positions in manager using grid coordinates
                    inventoryManager.updateItemPosition(
                        gameObject.itemCode,
                        closest.gridX,
                        closest.gridY
                    );
                    inventoryManager.updateItemPosition(
                        targetItem.itemCode,
                        originalSlot.gridX,
                        originalSlot.gridY
                    );
                } else {
                    // Move to empty slot
                    gameObject.x = closest.x;
                    gameObject.y = closest.y;
                    
                    const originalSlot = this.getSlotAtPosition(gameObject.originalX, gameObject.originalY);
                    if (originalSlot) {
                        originalSlot.occupied = false;
                        originalSlot.item = null;
                    }
                    
                    closest.occupied = true;
                    closest.item = gameObject;
                    gameObject.originalX = closest.x;
                    gameObject.originalY = closest.y;

                    // Update position in manager using grid coordinates
                    inventoryManager.updateItemPosition(
                        gameObject.itemCode,
                        closest.gridX,
                        closest.gridY
                    );
                }
            } else {
                // Return to original position
                gameObject.x = gameObject.originalX;
                gameObject.y = gameObject.originalY;
            }
            this.sound.play('rustle', { volume: 0.4 });
        });
    }

    setupTooltip() {
        const rarityColors = {
            'Junk': 0x808080,      // Grey for junk items
            'Normal': 0xFFFFFF,    // White for normal items
            'Uncommon': 0x1EFF00,  // Green for uncommon items
            'Rare': 0x0070DD,      // Blue for rare items
            'Epic': 0xA335EE,      // Purple for epic items
            'Legendary': 0xFF8000, // Orange for legendary items
            'Quest': 0xFFFF00      // Yellow/gold for quest items
        };
        
        this.tooltip = this.add.container(0, 0);
        this.tooltip.setAlpha(0.90);
        this.tooltip.setDepth(1000);
        
        this.tooltipBg = this.add.rectangle(0, 0, 155, 40, 0x78716C)
            .setOrigin(0)
            .setStrokeStyle(2, 0xffffff);
        this.tooltip.add(this.tooltipBg);
        
        this.titleText = this.add.text(3, 3, '', { 
            fontSize: '12px',
            fontStyle: 'bold',
            fill: '#ffffff',
            wordWrap: { width: 140 }
        });
        
        this.rarityTypeText = this.add.text(3, 20, '', {
            fontSize: '12px',
            fontStyle: 'italic',
        });
        
        this.valueText = this.add.text(3, 35, '', {
            fontSize: '12px',
            fill: '#ffffff'
        });
        
        this.descriptionText = this.add.text(3, 50, '', {
            fontSize: '12px',
            fill: '#ffffff',
            wordWrap: { width: 140 }
        });
        
        // Add consumption effect texts for consumables
        this.hungerEffectText = this.add.text(3, 35, '', {
            fontSize: '12px',
            fill: '#FFA500', // Orange color
            fontStyle: 'bold'
        });
        
        this.healEffectText = this.add.text(3, 50, '', {
            fontSize: '12px',
            fill: '#FF0000', // Red color
            fontStyle: 'bold'
        });
        
        this.tooltip.add([this.titleText, this.rarityTypeText, this.valueText, this.hungerEffectText, this.healEffectText, this.descriptionText]);
        this.tooltip.setVisible(false);

        this.input.on('gameobjectover', (pointer, gameObject) => {
            if (gameObject instanceof InventoryItem) {
                if (!this.draggingItem || (this.draggingItem && this.draggingItem !== gameObject)) {
                    this.updateTooltip(gameObject, rarityColors);
                    this.tooltip.setVisible(true);
                }
            }
        });

        this.input.on('gameobjectout', (pointer, gameObject) => {
            if (gameObject instanceof InventoryItem) {
                this.tooltip.setVisible(false);
            }
        });
    }

    setupContextMenu() {
        // Right-click handler for items - using pointerdown pattern from Phaser example
        this.input.on('pointerdown', (pointer) => {
            if (pointer.rightButtonDown()) {
                // Check if we clicked on an inventory item
                const objectsUnderPointer = this.input.hitTestPointer(pointer);
                const inventoryItem = objectsUnderPointer.find(obj => obj instanceof InventoryItem);
                
                if (inventoryItem) {
                    this.showContextMenu(inventoryItem, pointer.x, pointer.y);
                }
            } else {
                // Hide context menu on left click
                this.hideContextMenu();
            }
        });
    }

    hideContextMenu() {
        if (this.contextMenu) {
            try {
                this.contextMenu.destroy();
            } catch (error) {
                console.warn('Error destroying context menu:', error);
            }
            this.contextMenu = null;
        }
    }

    showContextMenu(item, x, y) {
        // Hide existing context menu
        this.hideContextMenu();

        // Get item data to determine available actions
        const itemData = itemManager.getItemData(item.itemCode);
        if (!itemData) {
            return;
        }

        const options = [];

        // Add consume option for consumables (fish) and crops (only if quantity > 0)
        if ((itemData.type === 'Consumable' || itemData.type === 'Crop') && item.quantity > 0) {
            
            // Different text for crops vs fish
            const eatText = itemData.type === 'Crop' ? `Eat ${itemData.name} (${this.cropConsumption.count}/3)` : `Eat ${itemData.name}`;
            
            options.push({
                text: eatText,
                callback: () => {
                    this.consumeItem(item);
                }
            });
        }
        
        // Add sell option for all items (only if quantity > 0)
        if (item.quantity > 0) {
            // Get correct value for crops vs other items
            let sellValue = itemData.value || 0;
            if (itemData.type === 'Crop' && itemData.produce && itemData.produce.value) {
                sellValue = itemData.produce.value;
            }
            
            if (sellValue > 0) {
                options.push({
                    text: `Sell for ¥${sellValue}`,
                    callback: () => this.sellItem(item)
                });
            }
        }

        // Only show context menu if there are options
        if (options.length > 0) {
            this.contextMenu = new ContextMenu(this, x, y, options);
            this.contextMenu.show(x, y);
        }
    }
    
    async sellItem(item) {
        // Hide context menu after action
        this.hideContextMenu();

        try {
            // Get item data for sell price
            const itemData = itemManager.getItemData(item.itemCode);
            if (!itemData) {
                console.error('Cannot sell item: no item data found');
                return;
            }
            
            // Get correct sell price for crops vs other items  
            let sellPrice = itemData.value || 0;
            if (itemData.type === 'Crop' && itemData.produce && itemData.produce.value) {
                sellPrice = itemData.produce.value;
            }
            
            if (!sellPrice || sellPrice <= 0) {
                console.error('Cannot sell item: no value data');
                return;
            }

            // Extract category code from itemCode
            const parts = item.itemCode.split('_');
            const category = parts[0];
            const itemId = parseInt(parts[1]);
            
            // Get category code
            let categoryCode = 0; // Default to Consumable
            if (itemManager.metadata && itemManager.metadata.categoryIndex) {
                const categoryEntry = Object.entries(itemManager.metadata.categoryIndex)
                    .find(([, name]) => name === category);
                if (categoryEntry) {
                    categoryCode = parseInt(categoryEntry[0]);
                }
            }

            // Get wallet address from game store
            const gameStore = this.game.registry.get('gameStore');
            if (!gameStore || !gameStore.walletAddress) {
                console.error('No wallet address found');
                return;
            }

            // Sell the item via GameService
            const gameService = window.gameService;
            if (!gameService) {
                throw new Error('GameService not available');
            }

            const result = await gameService.fetchApi('/api/inventory/sell', {
                method: 'POST',
                body: {
                    itemCode: itemId,
                    categoryCode: categoryCode,
                    quantity: 1,
                    sellPrice: sellPrice
                }
            });

            // Play sell item sound effect
            this.sound.play('sell-item', { volume: 0.5 });

            // Update StatsManager with new stats (including money) from backend
            if (this.statsManager && result.stats) {
                const oldStats = { ...this.statsManager.stats };
                this.statsManager.stats = { ...result.stats };
                this.statsManager.events.emit('stats-updated', this.statsManager.stats);

            }

            // Show sell feedback
            this.showSellFeedback(itemData, result.soldItem.sellPrice);

            // Refresh inventory and money display
            await inventoryManager.syncWithDatabase();
            this.handleInventoryUpdate();
            
            // Update money display (should now show the updated amount)
            this.updateMoneyDisplay();

        } catch (error) {
            console.error('Error selling item:', error);
            // Could show error message to user here
        }
    }
    
    showSellFeedback(itemData, sellPrice) {
        // Create temporary feedback text
        const feedbackText = this.add.text(128, 96, `Sold ${itemData.name} for ¥${sellPrice}!`, {
            fontSize: '12px',
            fontFamily: 'Arial',
            color: '#00FF00',
            stroke: '#000000',
            strokeThickness: 2,
            align: 'center'
        }).setOrigin(0.5);

        // Animate and remove feedback
        this.tweens.add({
            targets: feedbackText,
            y: 80,
            alpha: 0,
            duration: 2000,
            ease: 'Power2',
            onComplete: () => {
                feedbackText.destroy();
            }
        });
    }

    async consumeItem(item) {
        
        if (!this.statsManager) {
            console.error('StatsManager not available');
            return;
        }

        // Get current stats before consumption
        const currentStats = this.statsManager.getStats();

        // Hide context menu after consumption
        this.hideContextMenu();

        try {
            // Get item data to check if it's a crop or fish
            const itemData = itemManager.getItemData(item.itemCode);
            
            // Handle crops vs fish differently
            if (itemData.type === 'Crop') {
                await this.consumeCrop(item, itemData);
            } else {
                // Handle fish consumption (original logic)
                await this.consumeFish(item, itemData, currentStats);
            }
            
        } catch (error) {
            console.error('Error consuming item:', error);
        }
    }
    
    async consumeCrop(item, itemData) {

        // Play eat sound effect
        this.sound.play('eatItem', { volume: 0.5 });

        // Remove item from inventory first
        const parts = item.itemCode.split('_');
        const itemId = parseInt(parts[1]);
        const categoryCode = 0; // Crops are consumables

        // Get crop value from produce.value (crops store value there, not at root)
        const itemValue = (itemData.produce && itemData.produce.value) || itemData.value || 0;
        const itemType = itemData.type || '';


        const result = await this.statsManager.consumeItem(itemId, categoryCode, 0, itemData.rarity, itemValue, itemType);

        if (result) {
            // Increment crop consumption count
            this.cropConsumption.count++;
            
            // Clear existing timer if any
            if (this.cropConsumption.timer) {
                clearTimeout(this.cropConsumption.timer);
            }
            
            // Show crop consumption feedback
            this.showCropConsumptionFeedback(item, this.cropConsumption.count);
            
            // Check if we've eaten 3 crops
            if (this.cropConsumption.count >= 3) {
                // Restore 1 hunger
                const currentStats = this.statsManager.getStats();
                const newHunger = Math.min(currentStats.hunger + 1, currentStats.max_hunger);
                this.statsManager.updateStats({ hunger: newHunger });
                
                this.showHungerRestoredFeedback(item);
                
                // Reset counter
                this.cropConsumption.count = 0;
            } else {
                // Start 5-second timer to reset count
                this.cropConsumption.timer = setTimeout(() => {
                    this.cropConsumption.count = 0;
                    this.cropConsumption.timer = null;
                }, this.cropConsumption.TIMER_DURATION);
                
            }
            
            // Sync inventory
            await inventoryManager.syncWithDatabase();
            this.handleInventoryUpdate();
        }
    }
    
    async consumeFish(item, itemData, currentStats) {
        // Play eat sound effect
        this.sound.play('eatItem', { volume: 0.5 });

        // Original fish consumption logic
        const parts = item.itemCode.split('_');
        const category = parts[0];
        const itemId = parseInt(parts[1]);

        // Get category code
        let categoryCode = 0; // Default to Consumable
        if (itemManager.metadata && itemManager.metadata.categoryIndex) {
            const categoryEntry = Object.entries(itemManager.metadata.categoryIndex)
                .find(([, name]) => name === category);
            if (categoryEntry) {
                categoryCode = parseInt(categoryEntry[0]);
            }
        }

        // Consume the item via StatsManager
        const healAmount = itemData?.healAmount || 0;
        const rarity = itemData?.rarity || 'Normal';
        const itemValue = itemData?.value || 0;
        // For fish, use subtype first since it contains "Fish/Freshwater", then fall back to type
        const itemType = itemData?.subtype || itemData?.type || '';

        const result = await this.statsManager.consumeItem(itemId, categoryCode, healAmount, rarity, itemValue, itemType);
        
        if (result) {
            
            // Show consumption feedback
            this.showConsumptionFeedback(item, result.stats);
            
            // Sync inventory
            await inventoryManager.syncWithDatabase();
            this.handleInventoryUpdate();
        } else {
            // Show feedback for max stats
            this.showConsumptionFeedback(item, currentStats, true);
        }
    }

    calculateSafeTextPosition(item, baseOffsetY) {
        // Get item's grid position to determine edge cases
        const gridPos = this.pixelToGrid(item.x, item.y);
        const sceneWidth = this.cameras.main.width;
        const sceneHeight = this.cameras.main.height;
        
        let x = item.x;
        let y = item.y + baseOffsetY;
        
        // Adjust for edge cases
        
        // First column - move text right to avoid going off left edge
        if (gridPos.x === 0) {
            x = item.x + 15;
        }
        
        // Last column - move text left to avoid going off right edge  
        if (gridPos.x === 6) {
            x = item.x - 15;
        }
        
        // First row - move text down instead of up to avoid going off top edge
        if (gridPos.y === 0) {
            y = item.y + Math.abs(baseOffsetY) + 10;
        }
        
        // Bottom row - ensure text doesn't go off bottom
        if (gridPos.y === 4) {
            y = Math.min(y, sceneHeight - 30);
        }
        
        // Final bounds check to ensure text stays on screen
        x = Math.max(50, Math.min(x, sceneWidth - 50));
        y = Math.max(20, Math.min(y, sceneHeight - 20));
        
        return { x, y };
    }

    showCropConsumptionFeedback(item, count) {
        // Show crop eaten feedback with counter
        const feedbackText = `Crop eaten (${count}/3)`;
        const safePos = this.calculateSafeTextPosition(item, -25);
        
        const cropText = this.add.text(safePos.x, safePos.y, feedbackText, {
            fontSize: '12px',
            fill: '#90EE90', // Light green for crops
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0.5).setDepth(10000); // Higher than UI elements

        this.animateEffectText(cropText);
    }
    
    showHungerRestoredFeedback(item) {
        // Show hunger restored feedback
        const safePos = this.calculateSafeTextPosition(item, -45);
        
        const hungerText = this.add.text(safePos.x, safePos.y, '+1 Hunger!', {
            fontSize: '16px',
            fill: '#FFA500', // Orange for hunger
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0.5).setDepth(10000); // Higher than UI elements

        this.animateEffectText(hungerText, 200); // Slight delay for better visibility
    }

    showConsumptionFeedback(item, newStats, isMaxStats = false) {
        // Create temporary text to show consumption feedback
        const itemData = itemManager.getItemData(item.itemCode);
        if (!itemData) return;

        if (isMaxStats) {
            // Show single warning text
            const safePos = this.calculateSafeTextPosition(item, -25);
            
            const warningText = this.add.text(safePos.x, safePos.y, 'Already Full!', {
                fontSize: '14px',
                fill: '#ffaa00',
                fontStyle: 'bold',
                stroke: '#000000',
                strokeThickness: 2
            }).setOrigin(0.5).setDepth(10000); // Higher than UI elements

            this.animateEffectText(warningText);
        } else {
            // Show separate effect texts for better visibility
            let baseOffsetY = -30;
            
            // Hunger effect (only for non-junk items)
            if (itemData.rarity !== 'Junk') {
                const safePos = this.calculateSafeTextPosition(item, baseOffsetY);
                
                const hungerText = this.add.text(safePos.x, safePos.y, '+1 Hunger', {
                    fontSize: '14px',
                    fill: '#FFA500', // Orange
                    fontStyle: 'bold',
                    stroke: '#000000',
                    strokeThickness: 2
                }).setOrigin(0.5).setDepth(10000); // Higher than UI elements
                
                this.animateEffectText(hungerText, 0);
                baseOffsetY -= 20;
            }
            
            // Healing effect (if any)
            if (itemData.healAmount && itemData.healAmount > 0) {
                const safePos = this.calculateSafeTextPosition(item, baseOffsetY);
                
                const healText = this.add.text(safePos.x, safePos.y, `+${itemData.healAmount} HP`, {
                    fontSize: '14px',
                    fill: '#FF0000', // Red
                    fontStyle: 'bold',
                    stroke: '#000000',
                    strokeThickness: 2
                }).setOrigin(0.5).setDepth(10000); // Higher than UI elements
                
                this.animateEffectText(healText, 200); // Slight delay for staggered effect
            }
        }
    }

    animateEffectText(textObject, delay = 0) {
        // Initial scale-up animation for impact
        this.tweens.add({
            targets: textObject,
            scaleX: 1.2,
            scaleY: 1.2,
            duration: 150,
            delay: delay,
            ease: 'Back.out',
            yoyo: true,
            onComplete: () => {
                // Hold for visibility
                this.time.delayedCall(2000, () => {
                    // Animate upward and fade out
                    this.tweens.add({
                        targets: textObject,
                        y: textObject.y - 50,
                        alpha: 0,
                        duration: 1000,
                        ease: 'Power2',
                        onComplete: () => {
                            textObject.destroy();
                        }
                    });
                });
            }
        });
    }

    handleInventoryUpdate() {
        // Clear existing items
        this.slots.forEach(slot => {
            if (slot.item) {
                slot.item.destroy();
                slot.item = null;
                slot.occupied = false;
            }
        });

        // Repopulate with new data
        this.populateInventory();
        
        // Update money display in case stats changed
        this.updateMoneyDisplay();
    }

    populateInventory() {
        const allItems = inventoryManager.getInventory();
        
        // Filter out items with 0 or negative quantity and seedbags (they appear in SeedbagUI now)
        const items = allItems.filter(item => {
            return item.quantity > 0 && !item.itemCode.startsWith('Seeds_');
        });
        
        
        // Sort items to ensure consistent positioning (by item code for consistency)
        const sortedItems = items.sort((a, b) => a.itemCode.localeCompare(b.itemCode));
        
        // Track occupied positions to prevent overlaps
        const occupiedPositions = new Set();
        
        sortedItems.forEach((item, index) => {
            const itemConfig = this.getItemConfigFromCode(item.itemCode);
            if (!itemConfig) {
                console.warn('No config found for item:', item.itemCode);
                return;
            }
    
            let gridPos;
            let pixelPos;
            
            // Try to use stored grid position if valid and not occupied
            if (item.gridX !== null && item.gridY !== null) {
                const posKey = `${item.gridX},${item.gridY}`;
                if (!occupiedPositions.has(posKey) && 
                    item.gridX >= 0 && item.gridX < 7 && 
                    item.gridY >= 0 && item.gridY < 5) {
                    gridPos = { gridX: item.gridX, gridY: item.gridY };
                    occupiedPositions.add(posKey);
                }
            }
            
            // If no valid stored position, find the next available slot
            if (!gridPos) {
                gridPos = this.findNextEmptyGridPosition(occupiedPositions);
                if (!gridPos) {
                    console.warn('No empty slot found for item:', item.itemCode);
                    return;
                }
                occupiedPositions.add(`${gridPos.gridX},${gridPos.gridY}`);
                
                // Update the item position in inventory manager
                inventoryManager.updateItemPosition(item.itemCode, gridPos.gridX, gridPos.gridY);
            }
            
            pixelPos = this.gridToPixel(gridPos.gridX, gridPos.gridY);
    
            this.addItem(
                itemConfig.textureKey,
                itemConfig.name,
                pixelPos.x,
                pixelPos.y,
                itemConfig.rarity,
                itemConfig.type,
                itemConfig.description,
                itemConfig.value,
                item.itemCode,
                item.quantity
            );
        });
    }

    findNextEmptyGridPosition(occupiedPositions) {
        // Find first available position from left to right, top to bottom
        for (let row = 0; row < 5; row++) {
            for (let col = 0; col < 7; col++) {
                const posKey = `${col},${row}`;
                if (!occupiedPositions.has(posKey)) {
                    return { gridX: col, gridY: row };
                }
            }
        }
        return null;
    }

    findEmptySlot() {
        return this.slots.find(slot => !slot.occupied);
    }

    gridToPixel(gridX, gridY) {
        return {
            x: gridX * 32 + 32,
            y: gridY * 32 + 32
        };
    }

    pixelToGrid(pixelX, pixelY) {
        return {
            x: Math.floor((pixelX - 32) / 32),
            y: Math.floor((pixelY - 32) / 32)
        };
    }

    getSlotAtPosition(x, y) {
        return this.slots.find(slot => slot.x === x && slot.y === y);
    }

    getSlotAtGridPosition(gridX, gridY) {
        return this.slots.find(slot => slot.gridX === gridX && slot.gridY === gridY);
    }

    updateTooltip(gameObject, rarityColors) {
        const itemTitle = gameObject.quantity > 1 ? `${gameObject.itemName} (x${gameObject.quantity})` : gameObject.itemName;
        this.titleText.setText(itemTitle);
        this.titleText.setColor('#' + rarityColors[gameObject.rarity].toString(16));
        this.rarityTypeText.setText(`${gameObject.rarity} ${gameObject.itemType}`);
        this.rarityTypeText.setColor('#1C1917');
        
        // Get correct value for crops vs other items
        let itemValue = gameObject.value;
        if (gameObject.itemType === 'Crop') {
            // For crops, get the value from item data produce section
            const itemData = itemManager.getItemData(gameObject.itemCode);
            if (itemData && itemData.produce && itemData.produce.value) {
                itemValue = itemData.produce.value;
            }
        }
        
        this.valueText.setText(`Value: ¥${itemValue}`);
        this.valueText.setFontStyle("normal");
        
        // Calculate Y positions properly to avoid overlap
        let currentY = 35 + this.valueText.height; // Start after value text
        
        // Handle consumption effects for consumables and crops
        if (gameObject.itemType === 'Consumable' || gameObject.itemType === 'Crop') {
            // Get item data to check type
            const itemData = itemManager.getItemData(gameObject.itemCode);
            
            if (gameObject.itemType === 'Crop') {
                // Crops require 3 to restore 1 hunger
                this.hungerEffectText.setText('3 crops = +1 Hunger');
                this.hungerEffectText.setPosition(3, currentY);
                this.hungerEffectText.setVisible(true);
                currentY += this.hungerEffectText.height;
            } else if (gameObject.rarity !== 'Junk') {
                // Fish restore 1 hunger each (only for non-junk items)
                this.hungerEffectText.setText('+1 Hunger');
                this.hungerEffectText.setPosition(3, currentY);
                this.hungerEffectText.setVisible(true);
                currentY += this.hungerEffectText.height;
            } else {
                this.hungerEffectText.setVisible(false);
            }
            
            // Show heal effect if any (for fish)
            if (itemData && itemData.healAmount && itemData.healAmount > 0) {
                this.healEffectText.setText(`+${itemData.healAmount} HP`);
                this.healEffectText.setPosition(3, currentY);
                this.healEffectText.setVisible(true);
                currentY += this.healEffectText.height;
            } else {
                this.healEffectText.setVisible(false);
            }
        } else {
            this.hungerEffectText.setVisible(false);
            this.healEffectText.setVisible(false);
        }
        
        // Position description text after effects with some spacing
        this.descriptionText.setText(gameObject.description);
        this.descriptionText.setFontStyle("italic");
        this.descriptionText.setPosition(3, currentY + 5);
    
        // Get tooltip dimensions and position
        let totalHeight = 3 + 
            this.titleText.height + 
            this.rarityTypeText.height +
            this.valueText.height +
            this.descriptionText.height + 
            12;
        
        // Add height for visible effect texts
        if (this.hungerEffectText.visible) totalHeight += this.hungerEffectText.height;
        if (this.healEffectText.visible) totalHeight += this.healEffectText.height;
        
        const largestWidth = Math.max(
            this.titleText.width, 
            this.rarityTypeText.width, 
            this.valueText.width, 
            this.hungerEffectText.visible ? this.hungerEffectText.width : 0,
            this.healEffectText.visible ? this.healEffectText.width : 0,
            this.descriptionText.width
        );
    
        this.tooltipBg.setSize(largestWidth + 6, totalHeight);
    
        const PLAY_AREA_WIDTH = 256;
        const PLAY_AREA_HEIGHT = 192;
        const TOOLTIP_MARGIN = 5;
    
        let tooltipX = gameObject.x + 16;
        let tooltipY = gameObject.y + 16;
    
        // Adjust horizontal position
        if (tooltipX + largestWidth > PLAY_AREA_WIDTH) {
            tooltipX = gameObject.x - largestWidth - TOOLTIP_MARGIN;
        }
    
        if (tooltipX < 0) {
            tooltipX = TOOLTIP_MARGIN;
        }
    
        // Adjust vertical position
        if (tooltipY + totalHeight > PLAY_AREA_HEIGHT) {
            tooltipY = gameObject.y - totalHeight - TOOLTIP_MARGIN;
        }
    
        if (tooltipY < 0) {
            tooltipY = TOOLTIP_MARGIN;
        }
    
        this.tooltip.setPosition(tooltipX, tooltipY);
    }

    addItem(textureKey, itemName, x, y, rarity = 'Normal', itemType = 'Misc', description = 'A mysterious item', value = 0, itemCode, quantity = 1) {
        //console.log('Adding item:', { itemCode, textureKey, x, y });
    
        // Get complete item data from manager
        const itemData = itemManager.getItemData(itemCode);
        if (!itemData) {
            console.warn('Failed to get item data for:', itemCode);
            return;
        }
    
        // Ensure textures are loaded - fix loader null error
        if (!this.textures.exists(itemData.textureKey)) {
            console.warn('Texture not loaded:', itemData.textureKey);
            
            // Check if loader is available and not in a finished state
            if (this.load && this.load.state !== Phaser.Loader.COMPLETE) {
                try {
                    this.load.image(itemData.textureKey, itemData.spritePath);
                    this.load.once('complete', () => {
                        this.addItemWithTexture(itemData, x, y, quantity);
                    });
                    this.load.start();
                    return;
                } catch (error) {
                    console.error('Failed to load texture dynamically:', error);
                    // Fall back to using missing texture placeholder
                    this.addItemWithMissingTexture(itemData, x, y, quantity);
                    return;
                }
            } else {
                console.warn('Loader not available or already finished, using placeholder');
                this.addItemWithMissingTexture(itemData, x, y, quantity);
                return;
            }
        }
    
        this.addItemWithTexture(itemData, x, y, quantity);
    }
    
        addItemWithTexture(itemData, x, y, quantity) {
        // Create composed item sprite
        const result = itemManager.createItemSprite(this, itemData.id, x, y);
        if (!result || !result.sprite) {
            console.warn('Failed to create item sprite for:', itemData.id);
            return;
        }

        const { sprite, data } = result;

        // Create inventory item with composed sprite
        const item = new InventoryItem(
            this,
            x,
            y,
            sprite.texture.key,
            data.name,
            data.rarity,
            data.type,
            data.description,
            data.value
        );

        item.itemCode = itemData.id;
        item.quantity = quantity;
        
        // Store grid coordinates for the item
        const gridPos = this.pixelToGrid(x, y);
        item.gridX = gridPos.x;
        item.gridY = gridPos.y;
        
        // Make item interactive
        item.setInteractive();
        this.input.setDraggable(item);
        
        this.add.existing(item);
        
        const slot = this.getSlotAtPosition(x, y);
        if (slot) {
            slot.occupied = true;
            slot.item = item;
        }

        // Store original position for drag operations
        item.originalX = x;
        item.originalY = y;

        // Clean up temporary sprite
        sprite.destroy();
    }
    
    addItemWithMissingTexture(itemData, x, y, quantity) {
        console.warn('Creating placeholder item for missing texture:', itemData.textureKey);
        
        // Create a simple colored rectangle as placeholder
        const placeholderSprite = this.add.rectangle(x, y, 24, 24, 0x888888);
        placeholderSprite.setDepth(502);
        
        // Create inventory item with placeholder
        const item = new InventoryItem(
            this,
            x,
            y,
            null, // No texture key
            itemData.name || 'Unknown Item',
            itemData.rarity || 'Normal',
            itemData.type || 'Misc',
            itemData.description || 'Missing texture',
            itemData.value || 0
        );

        item.itemCode = itemData.id;
        item.quantity = quantity;
        
        // Store grid coordinates for the item
        const gridPos = this.pixelToGrid(x, y);
        item.gridX = gridPos.x;
        item.gridY = gridPos.y;
        
        // Make item interactive
        item.setInteractive();
        this.input.setDraggable(item);
        
        this.add.existing(item);
        
        const slot = this.getSlotAtPosition(x, y);
        if (slot) {
            slot.occupied = true;
            slot.item = item;
        }

        // Store original position for drag operations
        item.originalX = x;
        item.originalY = y;
    }

    getItemConfigFromCode(item) {
        // Handle both string itemCodes and inventory item objects
        const itemCode = typeof item === 'string' ? item : item.itemCode;
        return itemManager.getItemData(itemCode);
    }

    destroy() {
        // Clean up event listeners
        if (this.tooltip) {
            this.tooltip.destroy();
            this.tooltip = null;
        }

        // Clean up context menu
        this.hideContextMenu();

        // Remove inventory update subscription
        inventoryManager.events.removeListener('inventory-updated', this.handleInventoryUpdate, this);

        // Clean up slots
        this.slots.forEach(slot => {
            if (slot.rect) slot.rect.destroy();
            if (slot.item) slot.item.destroy();
        });
        this.slots = [];

        super.destroy();
    }

    shutdown() {
        // Clean up event listeners
        if (inventoryManager) {
            inventoryManager.events.off('inventory-updated', this.handleInventoryUpdate, this);
        }
        
        // Clean up crop consumption timer
        if (this.cropConsumption.timer) {
            clearTimeout(this.cropConsumption.timer);
            this.cropConsumption.timer = null;
            this.cropConsumption.count = 0;
        }
    }
}